import React, { useCallback, useEffect, useMemo, useState } from "react";
import adminApi from "@/lib/adminApi";
import { Search, Pencil, Copy, Send, ShieldCheck, ShieldX, RefreshCw, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import DoctorEditDrawer from "./DoctorEditDrawer";

const TABS = [
  { key: "all", label: "All" },
  { key: "pending", label: "Pending" },
  { key: "invited", label: "Invited" },
  { key: "active", label: "Active" },
  { key: "blocked", label: "Blocked" },
];

export default function AdminDoctors() {
  const [sp, setSp] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const status = sp.get("status") || "all";
  const page = Number(sp.get("page") || 1);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data, headers } = await adminApi.get("/admin/doctors", {
        params: { q: q || undefined, status: status === "all" ? undefined : status, page, page_size: 20 },
      });
      setRows(data);
      setTotal(Number(headers["x-total-count"] || 0));
    } catch (e) {
      toast.error("Failed to load doctors");
    } finally { setLoading(false); }
  }, [q, status, page]);

  useEffect(() => { load(); }, [load]);

  const setStatus = (s) => setSp((prev) => { const n = new URLSearchParams(prev); s === "all" ? n.delete("status") : n.set("status", s); n.delete("page"); return n; });
  const setPage = (p) => setSp((prev) => { const n = new URLSearchParams(prev); if (p <= 1) n.delete("page"); else n.set("page", String(p)); return n; });

  const counts = useMemo(() => ({ all: total }), [total]);

  const approve = async (d) => {
    setBusyId(d.id);
    try {
      const { data } = await adminApi.post(`/admin/doctors/${d.id}/approve`);
      if (data.email_sent) toast.success(`Approved & activation email sent to ${d.email}`);
      else toast.success(`Approved. Copy the activation link (email not sent — no Resend or sandbox mode).`);
      await navigator.clipboard.writeText(data.activation_link).catch(() => {});
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Approve failed"); }
    finally { setBusyId(null); }
  };

  const reject = async (d) => {
    if (!window.confirm(`Reject and block ${d.email}?`)) return;
    setBusyId(d.id);
    try {
      await adminApi.post(`/admin/doctors/${d.id}/reject`);
      toast.success("Rejected");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reject failed"); }
    finally { setBusyId(null); }
  };

  const toggleBlock = async (d) => {
    setBusyId(d.id);
    try {
      const { data } = await adminApi.post(`/admin/doctors/${d.id}/block`);
      toast.success(data.is_blocked ? "Blocked" : "Unblocked");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusyId(null); }
  };

  const resend = async (d) => {
    setBusyId(d.id);
    try {
      const { data } = await adminApi.post(`/admin/doctors/${d.id}/resend-activation`);
      if (data.email_sent) toast.success("Activation email re-sent");
      else toast.success("Activation link generated");
      await navigator.clipboard.writeText(data.activation_link).catch(() => {});
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusyId(null); }
  };

  const copyLink = async (d) => {
    try {
      const { data } = await adminApi.get(`/admin/doctors/${d.id}/activation-link`);
      await navigator.clipboard.writeText(data.activation_link);
      toast.success("Activation link copied");
    } catch (e) { toast.error(e?.response?.data?.detail || "No link available"); }
  };

  const showing = rows.length;
  const from = (page - 1) * 20 + 1;
  const to = (page - 1) * 20 + showing;

  return (
    <div className="max-w-7xl">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[#8A9992]">User Management</div>
          <h1 className="text-3xl font-semibold text-[#1F2A24] mt-1" style={{ fontFamily: "Instrument Sans" }}>
            Doctors {total > 0 && <span className="text-[#8A9992] font-normal">({total})</span>}
          </h1>
        </div>
        <div className="relative w-72">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A9992]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, email, phone"
            className="input-clean pl-9"
            data-testid="admin-doctors-search"
          />
        </div>
      </div>

      <div className="card-soft p-2 md:p-3 mb-4 inline-flex gap-1 flex-wrap">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setStatus(t.key)}
            data-testid={`admin-tab-${t.key}`}
            className={`pill-tab ${status === t.key ? "pill-tab-active" : "pill-tab-inactive"}`}
          >
            {t.label}{t.key === "all" && counts.all ? ` (${counts.all})` : ""}
          </button>
        ))}
      </div>

      <div className="card-soft overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-doctors-table">
            <thead>
              <tr className="bg-[#F7F4EC] text-[#5C7568] text-[11px] uppercase tracking-widest">
                <Th>Doctor</Th>
                <Th>Email</Th>
                <Th>Phone</Th>
                <Th>Status</Th>
                <Th>Created</Th>
                <Th className="text-right pr-6">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="py-12 text-center text-[#8A9992]"><Loader2 className="animate-spin inline mr-2" size={14} />Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={6} className="py-16 text-center text-[#8A9992]">No doctors match the filter.</td></tr>
              ) : (
                rows.map((d) => (
                  <tr key={d.id} className="border-t border-[#EFECE5] hover:bg-[#FDFBF7]" data-testid={`doctor-row-${d.id}`}>
                    <Td>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-[#0A4C36]/10 text-[#0A4C36] font-semibold flex items-center justify-center text-xs">
                          {(d.name || "?").slice(0, 1).toUpperCase()}
                        </div>
                        <div>
                          <div className="font-semibold text-[#1F2A24]">{d.name || "Unnamed"}</div>
                          <div className="text-[11px] text-[#8A9992]">{d.specialties?.join(", ") || "—"}</div>
                        </div>
                      </div>
                    </Td>
                    <Td>{d.email}</Td>
                    <Td>{d.mobile || <span className="text-[#B5C0BA]">—</span>}</Td>
                    <Td><StatusPill s={d.display_status} /></Td>
                    <Td className="text-[12px] text-[#6C7B72]">{d.created_at ? new Date(d.created_at).toLocaleDateString() : "—"}</Td>
                    <Td className="text-right pr-4">
                      <div className="inline-flex gap-1">
                        {d.display_status === "Pending" && (
                          <>
                            <IconBtn title="Approve & send activation" onClick={() => approve(d)} busy={busyId === d.id} tint="emerald" testId={`approve-${d.id}`}>
                              <ShieldCheck size={14} />
                            </IconBtn>
                            <IconBtn title="Reject" onClick={() => reject(d)} busy={busyId === d.id} tint="danger" testId={`reject-${d.id}`}>
                              <ShieldX size={14} />
                            </IconBtn>
                          </>
                        )}
                        {d.display_status === "Invited" && (
                          <>
                            <IconBtn title="Resend activation" onClick={() => resend(d)} busy={busyId === d.id} tint="peach" testId={`resend-${d.id}`}>
                              <Send size={14} />
                            </IconBtn>
                            <IconBtn title="Copy activation link" onClick={() => copyLink(d)} tint="neutral" testId={`copy-link-${d.id}`}>
                              <Copy size={14} />
                            </IconBtn>
                          </>
                        )}
                        {d.display_status === "Active" && (
                          <IconBtn title="Block" onClick={() => toggleBlock(d)} busy={busyId === d.id} tint="neutral" testId={`block-${d.id}`}>
                            <ShieldX size={14} />
                          </IconBtn>
                        )}
                        {d.display_status === "Blocked" && (
                          <IconBtn title="Unblock" onClick={() => toggleBlock(d)} busy={busyId === d.id} tint="emerald" testId={`unblock-${d.id}`}>
                            <RefreshCw size={14} />
                          </IconBtn>
                        )}
                        <IconBtn title="Edit" onClick={() => setEditing(d)} tint="neutral" testId={`edit-${d.id}`}>
                          <Pencil size={14} />
                        </IconBtn>
                      </div>
                    </Td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination footer */}
        <div className="flex items-center justify-between px-6 py-3 border-t border-[#EFECE5] text-xs text-[#6C7B72]">
          <div>{showing > 0 ? `${from}-${to} of ${total}` : "0 of 0"}</div>
          <div className="flex gap-2">
            <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="pill-tab pill-tab-inactive disabled:opacity-40" data-testid="admin-prev-page">Prev</button>
            <button disabled={page * 20 >= total} onClick={() => setPage(page + 1)} className="pill-tab pill-tab-inactive disabled:opacity-40" data-testid="admin-next-page">Next</button>
          </div>
        </div>
      </div>

      <DoctorEditDrawer
        doctor={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />
    </div>
  );
}

function StatusPill({ s }) {
  const map = {
    Active: { c: "text-[#0A4C36] bg-[#E6F0EC] border-[#0A4C36]/20", icon: <ShieldCheck size={11} /> },
    Pending: { c: "text-[#8A5A34] bg-[#FCE7D9] border-[#8A5A34]/20", icon: <Mail size={11} /> },
    "Pending OTP": { c: "text-[#5C3A21] bg-[#FCE7D9] border-[#5C3A21]/20", icon: <Mail size={11} /> },
    Invited: { c: "text-[#8A5A34] bg-[#FCE7D9] border-[#8A5A34]/20", icon: <Send size={11} /> },
    Blocked: { c: "text-[#B14A2A] bg-[#F8E7DF] border-[#B14A2A]/20", icon: <ShieldX size={11} /> },
  };
  const t = map[s] || { c: "text-[#6C7B72] bg-[#F7F4EC] border-[#EFECE5]" };
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${t.c}`} data-testid={`status-${s}`}>
      {t.icon} {s}
    </span>
  );
}

function IconBtn({ children, title, onClick, busy, tint, testId }) {
  const tints = {
    emerald: "text-[#0A4C36] hover:bg-[#E6F0EC]",
    peach: "text-[#8A5A34] hover:bg-[#FCE7D9]",
    danger: "text-[#B14A2A] hover:bg-[#F8E7DF]",
    neutral: "text-[#47554D] hover:bg-[#F3F1EC]",
  };
  return (
    <button
      title={title}
      onClick={onClick}
      disabled={busy}
      data-testid={testId}
      className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors disabled:opacity-40 ${tints[tint || "neutral"]}`}
    >
      {busy ? <Loader2 size={13} className="animate-spin" /> : children}
    </button>
  );
}

function Th({ children, className = "" }) {
  return <th className={`text-left px-4 py-3 font-semibold ${className}`}>{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`px-4 py-3 align-middle ${className}`}>{children}</td>;
}
